<template>
    <div ref="filterAnchor" aria-hidden="true" class="h-0 w-full" />

    <div
        ref="filterBar"
        class="filter-bar"
        :class="{ 'filter-bar--stuck': isStuck }"
        :style="isStuck ? { top: stuckTop + 'px' } : undefined"
    >
        <div :class="isStuck ? 'container px-3 mx-auto md:px-10 py-2' : ''">
            <div class="flex lg:hidden">
        <p-button
            @click="visibleFilter = true"
            size="small"
            severity="secondary"
            outlined
            rounded
            icon="pi pi-sliders-h"
            label="Filters"
            class="w-full"
        />
    </div>

    <div class="flex items-stretch gap-3 group">
        <div class="hidden lg:flex shrink-0 items-center pr-3 relative z-10 bg-white">
            <p-button
                @click="visibleFilter = true"
                size="small"
                severity="secondary"
                outlined
                rounded
                icon="pi pi-sliders-h"
                label="Filters"
            />
        </div>

        <div class="relative flex-1 min-w-0">
            <div
                v-show="canScrollLeft"
                class="hidden lg:block pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-white to-transparent transition-opacity"
            />
            <div
                v-show="canScrollRight"
                class="hidden lg:block pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-white to-transparent transition-opacity"
            />

            <button
                v-show="canScrollLeft"
                type="button"
                @click="scrollBy(-1)"
                aria-label="Scroll categories left"
                class="hidden lg:group-hover:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 items-center justify-center !rounded-full bg-white shadow-lg hover:bg-gray-50 transition"
            >
                <span class="pi pi-chevron-left text-primary text-sm" />
            </button>

            <div
                ref="scroller"
                @scroll="updateScrollState"
                class="flex items-stretch gap-3 overflow-x-auto scroll-smooth py-2 px-1 snap-x"
                style="scrollbar-width: none"
            >
                <button
                    v-for="tag in sortedCategories"
                    :key="tag.id"
                    type="button"
                    @click="toggleCategory(tag)"
                    :class="[
                        'snap-start shrink-0 inline-flex items-center gap-2 text-left !rounded-3xl border px-3 py-2 transition',
                        form.tour_category.includes(tag.slug)
                            ? 'border-primary bg-primary-50 ring-1 ring-primary'
                            : 'border-gray-200 bg-white hover:border-gray-300',
                    ]"
                >
                    <span class="text-sm font-semibold text-gray-900 leading-tight">
                        {{ tag.name }}
                    </span>
                    <span class="text-xs text-gray-500">
                        {{ formatCount(tag) }}
                    </span>
                </button>
            </div>

            <button
                v-show="canScrollRight"
                type="button"
                @click="scrollBy(1)"
                aria-label="Scroll categories right"
                class="hidden lg:group-hover:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 items-center justify-center !rounded-full bg-white shadow-lg border-gray-100 hover:bg-gray-50 transition"
            >
                <span class="pi pi-chevron-right text-primary text-sm" />
            </button>
        </div>
    </div>

    <div
        v-if="selectedCategoryTags.length"
        class="flex flex-wrap items-center gap-1.5 mt-1.5"
    >
        <button
            v-for="tag in selectedCategoryTags"
            :key="tag.slug"
            type="button"
            @click="toggleCategory(tag)"
            class="inline-flex items-center gap-1 px-2 py-0.5 !rounded-full bg-gray-50 border border-gray-200 text-gray-600 !text-[10px] hover:bg-gray-100 hover:text-gray-800 transition"
        >
            {{ tag.name }}
            <span class="pi pi-times !text-[8px] text-gray-400" />
        </button>
        <button
            v-if="selectedCategoryTags.length > 1"
            type="button"
            @click="clearCategories"
            class="!text-[10px] text-gray-400 hover:text-gray-600 ml-0.5"
        >
            Clear
        </button>
        </div>
        </div>
    </div>

    <!-- Spacer holds the layout open while the bar is pinned to the top -->
    <div v-show="isStuck" aria-hidden="true" :style="{ height: barHeight + 'px' }" />

    <drawer header="Refine Search" class="min-h-[90vh]" v-model:visible="visibleFilter" position="left">
        <div class="flex flex-col">
            <div class="flex flex-col gap-2">
                <label class="mb-2 text-sm font-semibold">Price Range</label>
                <slider class="w-full" v-model="form.price" range :max="meta.max_price" :min="meta.min_price"
                    :step="0.25" />
                <div class="flex items-center justify-between gap-5">
                    <tag :value="numeral(form.price[0]).format('$0,0.00')" rounded severity="secondary" />
                    <tag :value="numeral(form.price[1]).format('$0,0.00')" rounded severity="secondary" />
                </div>
            </div>
            <divider />
            <div class="flex flex-col gap-2">
                <label class="mb-2 text-sm font-semibold">Duration</label>
                <slider class="w-full" v-model="form.duration" range :min="meta.min_duration" :max="meta.max_duration"
                    :step="1" />
                <div class="flex items-center justify-between gap-5">
                    <tag :value="`${form.duration[0]} hrs`" rounded severity="secondary" />
                    <tag :value="`${form.duration[1]} hrs`" rounded severity="secondary" />
                </div>
            </div>
            <divider />
            <div class="flex flex-col gap-2">
                <label class="mb-2 text-sm font-semibold">Categories</label>

                <label v-for="tag of sortedCategories" :key="tag.id"
                    class="!flex !items-center !gap-3 cursor-pointer align-center">
                    <checkbox v-model="form.tour_category" name="category" :value="tag.slug" size="small" />
                    <span class="text-sm leading-none">{{ tag.name }}</span>
                </label>
            </div>
        </div>
        <template #footer>
            <form @submit.prevent="onSubmit" class="flex gap-2">
                <p-button @click="onReset" rounded fluid text size="small" label="Reset" icon="pi pi-times"
                    severity="secondary" />
                <p-button type="submit" fluid rounded size="small"
                    :label="form.processing ? `Applying Filters` : `Apply Filters`" :loading="form.processing" />
            </form>
        </template>
    </drawer>
</template>

<script setup lang="ts">
import numeral from "numeral";
import { Tag as TTag } from "@/types";
import { useForm } from "@inertiajs/vue3";
import {
    Button as PButton,
    Slider,
    Drawer,
    Divider,
    Tag,
    Checkbox,
} from "primevue";
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

const visibleFilter = ref(false);

const sortedCategories = computed(() =>
    [...(props.meta?.tour_categories ?? [])].sort((a, b) => a.name.localeCompare(b.name))
);

const selectedCategoryTags = computed<TTag[]>(() =>
    sortedCategories.value.filter((t: TTag) =>
        form.tour_category.includes(t.slug)
    )
);

const clearCategories = function () {
    form.tour_category = [];
    onSubmit();
};

const emit = defineEmits(["submit"]);

const { data, ...props } = defineProps<{
    data: { [key: string]: any };
    meta?:
    | {
        tour_categories: TTag[];
        tour_count_per_category?: Record<string, number>;
        min_price: number;
        max_price: number;
        min_duration: number;
        max_duration: number;
        min_rating: number;
        max_rating: number;
    }
    | any;
}>();

const form = useForm({
    price: [
        parseFloat(data?.min_price ?? props.meta?.min_price ?? 0),
        parseFloat(data?.max_price ?? props.meta?.max_price ?? 99999),
    ],
    duration: [
        parseFloat(data?.min_duration ?? props.meta?.min_duration ?? 0),
        parseFloat(data?.max_duration ?? props.meta?.max_duration ?? 10),
    ],
    tour_category: data?.tour_category ?? [],
});

const onSubmit = function () {
    visibleFilter.value = false;
    const { price, duration, ...rest } = form.data();
    emit("submit", {
        ...rest,
        min_price: price[0],
        max_price: price[1],
        min_duration: duration[0],
        max_duration: duration[1],
    });
};

const onReset = function () {
    visibleFilter.value = false;
    emit("submit", {}, true);
};

const toggleCategory = function (tag: TTag) {
    if (form.tour_category.includes(tag.slug))
        form.tour_category = form.tour_category.filter(
            (t: string) => t !== tag.slug
        );
    else form.tour_category = [...form.tour_category, tag.slug];

    onSubmit();
};

const formatCount = function (tag: TTag) {
    const count = props.meta?.tour_count_per_category?.[tag.slug];
    if (count == null) return "Explore tours";
    return `${count} ${count === 1 ? "experience" : "experiences"}`;
};

const scroller = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const updateScrollState = function () {
    const el = scroller.value;
    if (!el) return;
    canScrollLeft.value = el.scrollLeft > 4;
    canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
};

const scrollBy = function (direction: number) {
    const el = scroller.value;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
};

const filterAnchor = ref<HTMLElement | null>(null);
const filterBar = ref<HTMLElement | null>(null);
const isStuck = ref(false);
const barHeight = ref(0);
const stuckTop = ref(0);
let filterObserver: IntersectionObserver | null = null;
let topbarEl: HTMLElement | null = null;
let rafId = 0;

// Sit flush beneath the topbar (nav + breadcrumb), tracking its live position so
// the bar follows the topbar as it auto-hides and stays correct after an Inertia
// reload. Measuring live avoids a stale offset cached at mount time.
const measureTopbarOffset = function () {
    if (!topbarEl || !topbarEl.isConnected) {
        topbarEl = document.querySelector<HTMLElement>(".topbar");
    }
    const next = topbarEl
        ? Math.max(0, topbarEl.getBoundingClientRect().bottom)
        : 0;
    if (next !== stuckTop.value) stuckTop.value = next;
};

// While pinned, re-measure every frame so the bar stays locked to the topbar
// throughout its auto-hide/show transform — not just on discrete scroll ticks.
const trackOffset = function () {
    measureTopbarOffset();
    rafId = requestAnimationFrame(trackOffset);
};

const startTracking = function () {
    if (!rafId) trackOffset();
};

const stopTracking = function () {
    if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
    }
};

onMounted(() => {
    nextTick(updateScrollState);

    topbarEl = document.querySelector<HTMLElement>(".topbar");

    if (filterAnchor.value && "IntersectionObserver" in window) {
        filterObserver = new IntersectionObserver(
            ([entry]) => {
                const stuck =
                    !entry.isIntersecting && entry.boundingClientRect.top < 0;
                // Capture the in-flow height before pinning so the spacer can
                // hold the layout open and avoid a content jump.
                if (stuck && !isStuck.value && filterBar.value) {
                    barHeight.value = filterBar.value.offsetHeight;
                }
                isStuck.value = stuck;
                if (stuck) startTracking();
                else stopTracking();
            },
            { threshold: 0 }
        );
        filterObserver.observe(filterAnchor.value);
    }
});

onBeforeUnmount(() => {
    filterObserver?.disconnect();
    filterObserver = null;
    stopTracking();
    topbarEl = null;
});
</script>

<style scoped>
div::-webkit-scrollbar {
    display: none;
}

.filter-bar--stuck {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 900;
    background: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    animation: filter-bar-drop 0.25s ease;
}

@keyframes filter-bar-drop {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
